"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { toast } from "sonner";
import { useEffect } from "react";
export default function ContactUs() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  return <section ref={ref} className="min-h-screen flex items-center justify-center bg-background px-4 py-16" style={{
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(1.5rem)",
    transition: "opacity 200ms ease-out, transform 200ms ease-out"
  }}>
      <FormProvider className="w-full max-w-6xl bg-surface/80 backdrop-blur-md border border-muted rounded-lg shadow-medium p-8 md:p-12" formId="contact-form-hero" sectionName="contact-us-section" defaultValues={{
      name: "",
      email: "",
      message: ""
    }} validationRules={{
      name: {
        required: true,
        minLength: 2,
        maxLength: 50
      },
      email: {
        required: true,
        pattern: "email"
      },
      message: {
        required: true,
        minLength: 10
      }
    }} submitText="Envoyer le message" successMessage="Merci ! Nous vous répondrons bientôt.">
        <ContactForm />
      </FormProvider>
    </section>;
}
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  useEffect(() => {
    if (isSubmitted && !submitError) {
      toast.success(successMessage, {
        duration: 4000,
        style: {
          background: "hsl(220, 80%, 50%)",
          color: "hsl(0, 0%, 100%)",
          border: "none"
        }
      });
    }
  }, [isSubmitted, submitError, successMessage]);
  if (isSubmitted && !submitError) {
    return <div className="text-center py-12">
        <h2 className="text-3xl font-display font-bold text-foreground mb-4">
          Message envoyé avec succès
        </h2>
        <p className="text-muted-foreground mb-6">{successMessage}</p>
        <Button onClick={resetForm} className="bg-primary hover:bg-primary-hover text-foreground-primary px-6 py-3 rounded-md font-semibold transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-105 hover:shadow-medium">
          Envoyer un autre message
        </Button>
      </div>;
  }
  return <div>
      <h2 className="text-[1.8rem] md:text-[2.4rem] font-display font-bold text-foreground mb-2 text-center">
        Contactez-nous
      </h2>
      <p className="text-muted-foreground text-center mb-8">
        Prenez contact pour démarrer une conversation stratégique
      </p>

      <div className="grid md:grid-cols-2 gap-8 items-start">
        <Form onSubmit={handleSubmit} className="space-y-6">
          <FormField control={control} name="name" rules={{
          required: "Le nom est requis",
          minLength: {
            value: 2,
            message: "Minimum 2 caractères"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-semibold">
                  Nom
                </FormLabel>
                <FormControl>
                  <Input placeholder="Votre nom" {...field} className="bg-background/40 border-muted text-foreground placeholder:text-muted-foreground focus:border-primary transition-all duration-150 focus:shadow-[0_2px_0_0_hsl(220,80%,50%)]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" rules={{
          required: "L'email est requis",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Format d'email invalide"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-semibold">
                  Email
                </FormLabel>
                <FormControl>
                  <Input type="email" placeholder="vous@exemple.com" {...field} className="bg-background/40 border-muted text-foreground placeholder:text-muted-foreground focus:border-primary transition-all duration-150 focus:shadow-[0_2px_0_0_hsl(220,80%,50%)]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="message" rules={{
          required: "Le message est requis",
          minLength: {
            value: 10,
            message: "Minimum 10 caractères"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground font-semibold">
                  Message
                </FormLabel>
                <FormControl>
                  <Textarea placeholder="Votre message..." {...field} className="bg-background/40 border-muted text-foreground placeholder:text-muted-foreground focus:border-primary transition-all duration-150 focus:shadow-[0_2px_0_0_hsl(220,80%,50%)] min-h-[6.4rem]" />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          {submitError && <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-red-400 text-sm">{submitError}</p>
            </div>}
        </Form>

        <div className="flex items-center justify-center md:justify-start h-full">
          <Button type="submit" onClick={handleSubmit} disabled={isSubmitting} className="bg-primary hover:bg-primary-hover text-foreground-primary px-8 py-5 rounded-md font-semibold text-lg transition-all duration-[var(--duration-medium)] ease-[var(--ease-spring)] hover:scale-105 hover:shadow-medium disabled:opacity-50 disabled:cursor-not-allowed w-full md:w-auto">
            {isSubmitting ? <>
                <Loader className="animate-spin mr-2 size-4" />
                Envoi en cours...
              </> : "Démarrons "}
          </Button>
        </div>
      </div>
    </div>;
}